// src/avansuri.js
// CRUD minimal pentru avansuri: cine, cat, cand.
// Netarea cu orele confirmate se face separat, in RaportOre.
import * as React from "react";
import {
    List, Datagrid, TextField, DateField, FunctionField,
    Create, Edit, SimpleForm, TextInput, DateInput, NumberInput,
    ReferenceInput, SelectInput, required, minValue,
    TopToolbar, CreateButton, FilterButton,
    useGetList, useRecordContext,
} from "react-admin";
import { useCanManage, getColaboratorId } from "./usePermissions";

const optiuneColaborator = (record) =>
    record ? `${record.nume} ${record.prenume}` : "";

// API-ul intoarce colaboratorul ca IRI ("/api/colaboratori/7"), iar
// normalizeRecord pune id numeric pe inregistrarile aduse. <ReferenceField>
// cauta apoi rezultatul dupa cheia originala (string-ul IRI) si nu-l gaseste,
// asa ca lasa celula goala fara sa semnaleze nimic. Rezolvam facand noi
// potrivirea, pe id numeric.
const idNumeric = (valoare) => {
    if (valoare == null) return null;
    if (typeof valoare === "number") return valoare;
    const potrivire = String(valoare).match(/(\d+)$/);
    return potrivire ? parseInt(potrivire[1], 10) : null;
};

// API Platform vrea IRI pentru relatii, dar SelectInput produce id numeric.
// Conversia e idempotenta, ca sa poata fi aplicata si peste o valoare deja IRI.
const laIri = (valoare, resursa) => {
    if (valoare == null || valoare === "") return valoare;
    if (typeof valoare === "string" && valoare.startsWith("/api/")) return valoare;
    return `/api/${resursa}/${valoare}`;
};

const transformaAvans = (data) => ({
    ...data,
    colaborator: laIri(data.colaborator, "colaboratori"),
});

const ColaboratorField = ({ colaboratoriDupaId }) => {
    const record = useRecordContext();
    const colab = colaboratoriDupaId?.[idNumeric(record?.colaborator)];
    return <span>{colab ? optiuneColaborator(colab) : "—"}</span>;
};

const filtre = [
    <ReferenceInput key="colaborator" source="colaborator" reference="colaboratori" alwaysOn>
        <SelectInput label="Colaborator" optionText={optiuneColaborator} emptyText="Toti" />
    </ReferenceInput>,
    <DateInput key="dupa" source="data[after]" label="De la" />,
    <DateInput key="inainte" source="data[before]" label="Pana la" />,
];

// FilterButton arunca daca <List filters> nu e setat, iar filtrele exista doar
// pentru admin. Butonul de creare insa trebuie sa apara pentru toata lumea.
const Actiuni = ({ cuFiltre }) => (
    <TopToolbar>
        {cuFiltre && <FilterButton />}
        <CreateButton />
    </TopToolbar>
);

export const AvansList = () => {
    const canManage = useCanManage();
    const colaboratorId = getColaboratorId();

    // O singura cerere pentru toti colaboratorii, nu una pe rand.
    const { data: colaboratori } = useGetList("colaboratori", {
        pagination: { page: 1, perPage: 500 },
        sort: { field: "nume", order: "ASC" },
    });

    const colaboratoriDupaId = React.useMemo(() => {
        const harta = {};
        (colaboratori || []).forEach((c) => { harta[idNumeric(c.id)] = c; });
        return harta;
    }, [colaboratori]);

    // Un colaborator simplu isi vede doar propriile avansuri.
    const filtruPermanent = canManage
        ? {}
        : { colaborator: `/api/colaboratori/${colaboratorId}` };

    return (
        <List
            filters={canManage ? filtre : undefined}
            filter={filtruPermanent}
            actions={<Actiuni cuFiltre={canManage} />}
            sort={{ field: "data", order: "DESC" }}
            title="Avansuri"
        >
            <Datagrid rowClick={canManage ? "edit" : false} bulkActionButtons={false}>
                <DateField source="data" label="Data" locales="ro-RO" />
                <ColaboratorField label="Colaborator" colaboratoriDupaId={colaboratoriDupaId} />
                {/* suma vine din API ca string (decimal Doctrine), iar NumberField
                    formateaza doar numere - fara conversie s-ar afisa brut. */}
                <FunctionField
                    source="suma"
                    label="Suma"
                    textAlign="right"
                    render={(record) => {
                        const val = parseFloat(record?.suma);
                        return Number.isFinite(val)
                            ? val.toLocaleString("ro-RO", { style: "currency", currency: "RON" })
                            : "—";
                    }}
                />
                <TextField source="observatii" label="Observatii" />
            </Datagrid>
        </List>
    );
};

const Formular = () => {
    const canManage = useCanManage();
    const colaboratorId = getColaboratorId();

    return (
    <SimpleForm>
        {canManage ? (
            <ReferenceInput source="colaborator" reference="colaboratori">
                <SelectInput
                    label="Colaborator"
                    optionText={optiuneColaborator}
                    validate={required("Alege colaboratorul")}
                    format={(v) => idNumeric(v) ?? ""}
                    fullWidth
                />
            </ReferenceInput>
        ) : (
            // Colaboratorul simplu isi inregistreaza doar avansul propriu.
            // Backend-ul respinge oricum un alt colaborator (securityPostDenormalize),
            // asta doar scuteste un 403 inutil.
            <TextInput
                source="colaborator"
                defaultValue={`/api/colaboratori/${colaboratorId}`}
                style={{ display: "none" }}
            />
        )}
        <DateInput
            source="data"
            label="Data primirii"
            defaultValue={new Date().toISOString().slice(0, 10)}
            validate={required("Data este obligatorie")}
        />
        <NumberInput
            source="suma"
            label="Suma (lei)"
            step={0.01}
            min={0.01}
            validate={[required("Suma este obligatorie"), minValue(0.01, "Suma trebuie sa fie pozitiva")]}
        />
        <TextInput source="observatii" label="Observatii" fullWidth multiline />
    </SimpleForm>
    );
};

export const AvansCreate = () => (
    <Create title="Avans nou" redirect="list" transform={transformaAvans}>
        <Formular />
    </Create>
);

// Editarea si stergerea raman exclusiv la admin (backend-ul le refuza pentru
// restul), asa incat un avans inregistrat de colaborator nu poate fi retras
// ulterior de el.
export const AvansEdit = () => (
    <Edit title="Modifica avans" redirect="list" mutationMode="pessimistic" transform={transformaAvans}>
        <Formular />
    </Edit>
);
